import type { DataGridColumn, DataGridFilterConfig, DataGridGroupColumn, DataGridLeafColumn } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { ATTRIBUTE_TYPES, DISPLAY_FORMATS } from '@/entities/attributes';
import {
  ConversationListItem,
  loadConversationClientOptions,
  loadConversationEmployeeOptions,
  PERSON_ROLES,
  resolveConversationClientOptions,
  resolveConversationEmployeeOptions,
} from '@/entities/conversations';
import { getAttrValueByPath, renderConversationAttrValue } from '@/entities/conversations/model/utils';
import { EXPERT_REPORT_STATUS, getExpertReportStatusLabel } from '@/entities/expert-report';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { getShortName } from '@/shared/lib';
import type { DialogColumnMeta } from './columnMeta';
import { ATTRIBUTE_COLUMN_PREFIX, EMPTY_CELL, EXPERT_REPORT_STANDARD_COLUMNS } from './constants';
import { getQualityZoneCellClassName, getRatingCellClassName } from './ratingCellClassName';
import type { ContentSearchColumnOptions, ExpertReportBlocksMap } from './useDialogTableColumns';
import {
  getSnippetColumnId,
  getSnippetValue,
  pickContentSearchColumns,
  renderRelevanceCell,
  renderSnippetCell,
} from './contentSearchColumnHelpers';

/** Параметры хука построения колонок DataGrid для страницы диалогов */
type UseDialogDataGridColumnsParams = {
  /** Все доступные атрибуты — включаются в колонки целиком; видимость управляется снаружи */
  allAttributes: ConversationAttributeItem[];
  /** Все доступные экспертные отчёты — каждый отображается как отдельная группа колонок */
  allReports: { id: number; name: string }[];
  /** Соответствие reportId → (blockId → block) для подписей колонок блоков */
  blocksByReportId: ExpertReportBlocksMap;
  /** Колонки «Релевантность» + фрагмент появляются при активном режиме поиска по содержимому диалога */
  contentSearch?: ContentSearchColumnOptions;
};

const getPersonByRole = (
  item: ConversationListItem,
  role: (typeof PERSON_ROLES)[keyof typeof PERSON_ROLES],
): string => {
  const persons = item.persons || [];
  if (persons?.length) {
    const person = persons.find((p) => p.role === role);
    if (person) {
      if (person.fio) return person.fio;
      return getShortName(person.firstName, person.lastName, person.middleName);
    }
  }
  return '';
};

/** Конвертирует тип атрибута в DataGridFilterConfig */
const getAttributeDataGridFilter = (attr: ConversationAttributeItem): DataGridFilterConfig => {
  if (attr.dataType === ATTRIBUTE_TYPES.SELECT || attr.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    return {
      // Бэкенд принимает и скаляр, и массив (см. SearchConversationFilters.attributes).
      type: 'multiselect',
      options: attr.selectValues?.map((o) => ({ value: o.code, label: o.value })) ?? [],
    };
  }
  if (attr.dataType === ATTRIBUTE_TYPES.INTEGER || attr.dataType === ATTRIBUTE_TYPES.DECIMAL) {
    if (attr.displayFormat === DISPLAY_FORMATS.SECOND) {
      return { type: 'time-range', returnType: 'seconds', timeFormat: 'MM:SS' };
    }
    return { type: 'integer' };
  }
  if (attr.dataType === ATTRIBUTE_TYPES.DATE) return { type: 'date-range' };
  if (attr.dataType === ATTRIBUTE_TYPES.TIMESTAMP) return { type: 'timestamp-range' };
  if (attr.dataType === ATTRIBUTE_TYPES.CHECKBOX) return { type: 'boolean' };
  return { type: 'text' };
};

/**
 * Хук построения колонок DataGrid для таблицы диалогов.
 * Все основные колонки всегда включены; атрибуты включаются целиком — видимость управляется снаружи.
 */
export const useDialogDataGridColumns = ({
  allAttributes,
  allReports,
  blocksByReportId,
  contentSearch,
}: UseDialogDataGridColumnsParams): DataGridColumn<ConversationListItem>[] => {
  const { t, i18n } = useTranslation(['dialogs']);

  const getReportRating = useCallback(
    (item: ConversationListItem, reportId: number): { value: number | null; max: number | null } => {
      const arr = item.conversation2expertReport;
      if (!arr?.length) return { value: null, max: null };
      const found = arr.find((r) => r.expertReport.id === reportId);
      return { value: found?.rating ?? null, max: found?.maxRating ?? null };
    },
    [],
  );

  const getBlockScore = useCallback(
    (item: ConversationListItem, reportId: number, blockId: number): { value: number | null; max: number | null } => {
      const arr = item.conversation2expertReport;
      if (!arr?.length) return { value: null, max: null };
      const record = arr.find((r) => r.expertReport.id === reportId);
      if (!record?.blocks) return { value: null, max: null };
      const block = record.blocks.find((b) => b.blockId === blockId);
      if (!block) return { value: null, max: null };
      return { value: block.score ?? null, max: block.maxScore ?? null };
    },
    [],
  );

  const mainColumns = useMemo((): DataGridLeafColumn<ConversationListItem>[] => [
    {
      id: 'id',
      accessor: 'id',
      header: t('dialogListSection.id'),
      size: 80,
      enableSorting: true,
      enableFiltering: true,
      filter: { type: 'integer' },
      cell: ({ value }) => <span className="font-roboto">{value as number}</span>,
    },
    {
      id: 'externalId',
      accessor: 'externalId',
      header: t('dialogListSection.externalId'),
      size: 160,
      enableSorting: true,
      enableFiltering: true,
      filter: { type: 'text' },
      cell: ({ value }) => <span className="font-roboto">{(value as string | null) ?? EMPTY_CELL}</span>,
    },
    {
      id: 'createdAt',
      accessor: 'createdAt',
      header: t('dialogListSection.date'),
      size: 150,
      enableSorting: true,
      enableFiltering: true,
      filter: { type: 'date-range' },
      cell: ({ value }) => (
        <span className="font-roboto">{formatHumanDate(i18n, value as string, { withTime: true })}</span>
      ),
    },
    {
      id: 'client',
      header: t('dialogListSection.client'),
      size: 150,
      enableSorting: true,
      enableFiltering: true,
      // Справочник участников целиком не выгружается: список опций грузится страницами с поиском
      // на бэкенде, а подписи уже выбранных участников догружаются точечной выборкой по их id.
      filter: {
        type: 'multiselect',
        loadOptions: loadConversationClientOptions,
        resolveOptions: resolveConversationClientOptions,
      },
      meta: { personRole: 'client' } satisfies DialogColumnMeta,
      cell: ({ row }) => <div className="line-clamp-2">{getPersonByRole(row, PERSON_ROLES.CLIENT)}</div>,
    },
    {
      id: 'employeeId',
      header: t('dialogListSection.consultant'),
      size: 150,
      enableSorting: true,
      enableFiltering: true,
      filter: {
        type: 'multiselect',
        loadOptions: loadConversationEmployeeOptions,
        resolveOptions: resolveConversationEmployeeOptions,
      },
      meta: { personRole: 'employee' } satisfies DialogColumnMeta,
      cell: ({ row }) => <div className="line-clamp-2">{getPersonByRole(row, PERSON_ROLES.EMPLOYEE)}</div>,
    },
    {
      id: 'hasTranscription',
      accessor: 'hasTranscription',
      header: t('dialogListSection.hasTranscription'),
      size: 160,
      enableSorting: true,
      enableFiltering: true,
      filter: { type: 'boolean' },
      cell: ({ value }) => {
        if (value === true) return <span>{t('hasTranscriptionYes')}</span>;
        if (value === false) return <span>{t('hasTranscriptionNo')}</span>;
        return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
      },
    },
  ], [t, i18n]);

  const reportColumns = useMemo((): DataGridColumn<ConversationListItem>[] => {
    return allReports.map((report) => {
      const rId = report.id;
      const subColumns: DataGridLeafColumn<ConversationListItem>[] = [];

      EXPERT_REPORT_STANDARD_COLUMNS.forEach((column) => {
        const colId = `expertReport_${rId}_${column}`;

        if (column === 'id') {
          subColumns.push({
            id: colId,
            header: t('dialogs:expertReport.id'),
            size: 100,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'integer' },
            cell: ({ row }) => {
              const item = row.conversation2expertReport?.find((r) => r.expertReport.id === rId);
              if (!item?.id) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return <span className="font-roboto">{item.id}</span>;
            },
          });
          return;
        }

        if (column === 'startedAt') {
          subColumns.push({
            id: colId,
            header: t('dialogs:dialogListSection.expertReport.startedAt'),
            size: 160,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'date-range' },
            cell: ({ row }) => {
              const item = row.conversation2expertReport?.find((r) => r.expertReport.id === rId);
              if (!item?.startedAt) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return <span className="font-roboto">{formatHumanDate(i18n, item.startedAt, { withTime: true })}</span>;
            },
          });
          return;
        }

        if (column === 'finishedAt') {
          subColumns.push({
            id: colId,
            header: t('dialogs:dialogListSection.expertReport.finishedAt'),
            size: 160,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'date-range' },
            cell: ({ row }) => {
              const item = row.conversation2expertReport?.find((r) => r.expertReport.id === rId);
              if (!item?.finishedAt) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return <span className="font-roboto">{formatHumanDate(i18n, item.finishedAt, { withTime: true })}</span>;
            },
          });
          return;
        }

        if (column === 'rating') {
          subColumns.push({
            id: colId,
            header: t('dialogs:expertReport.total'),
            size: 110,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'integer' },
            cellClassName: (ctx) => {
              const { value, max } = getReportRating(ctx.row, rId);
              return getQualityZoneCellClassName(value, max);
            },
            cell: ({ row }) => {
              const { value } = getReportRating(row, rId);
              if (value == null) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return value;
            },
          });
          return;
        }

        if (column === 'status') {
          subColumns.push({
            id: colId,
            header: t('dialogs:dialogListSection.expertReport.status'),
            size: 130,
            enableFiltering: true,
            filter: {
              // conversation2expertReport, набор разворачивается в несколько элементов (OR) —
              // см. buildConversation2ExpertReportFilters
              type: 'multiselect',
              options: (Object.values(EXPERT_REPORT_STATUS) as (keyof typeof EXPERT_REPORT_STATUS)[]).map((s) => ({
                value: s,
                label: getExpertReportStatusLabel(t, s),
              })),
            },
            cell: ({ row }) => {
              const item = row.conversation2expertReport?.find((r) => r.expertReport.id === rId);
              if (!item?.status) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return getExpertReportStatusLabel(t, item.status);
            },
          });
        }
      });

      // Блоки отчёта: появляются по мере загрузки
      const reportBlocks = blocksByReportId.get(rId);
      reportBlocks?.forEach((blockInfo, blockId) => {
        subColumns.push({
          id: `expertReport_${rId}_block_${blockId}`,
          header: blockInfo.name,
          size: 120,
          enableSorting: true,
          enableFiltering: true,
          filter: { type: 'integer' },
          cellClassName: (ctx) => {
            const { value, max } = getBlockScore(ctx.row, rId, blockId);
            return getRatingCellClassName(value, max);
          },
          cell: ({ row }) => {
            const { value } = getBlockScore(row, rId, blockId);
            if (value == null) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
            return value;
          },
        });
      });

      return {
        id: `expertReport_${rId}`,
        header: report.name,
        columns: subColumns,
      } as DataGridGroupColumn<ConversationListItem>;
    });
  }, [allReports, blocksByReportId, getReportRating, getBlockScore, i18n, t]);

  const attributeColumns = useMemo((): DataGridColumn<ConversationListItem>[] => {
    const ungrouped: DataGridLeafColumn<ConversationListItem>[] = [];
    // code группы → { name, columns }
    const groupedMap = new Map<string, { name: string; columns: DataGridLeafColumn<ConversationListItem>[] }>();

    for (const attr of allAttributes) {
      // id содержит префикс атрибутной колонки — так его ожидает useSmartSearchLazy при фильтрации
      const leafColumn: DataGridLeafColumn<ConversationListItem> = {
        id: `${ATTRIBUTE_COLUMN_PREFIX}${attr.path}`,
        header: attr.name,
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        filter: getAttributeDataGridFilter(attr),
        cell: ({ row }) => {
          const attrValue = getAttrValueByPath(row.conversationAttrValues, attr.path);
          return renderConversationAttrValue(attrValue, attr);
        },
      };

      // Атрибут группируется по первой группе; без группы — плоский
      const firstGroup = attr.attributeGroups?.[0];
      if (!firstGroup) {
        ungrouped.push(leafColumn);
      } else {
        const existing = groupedMap.get(firstGroup.code);
        if (existing) {
          existing.columns.push(leafColumn);
        } else {
          groupedMap.set(firstGroup.code, { name: firstGroup.name, columns: [leafColumn] });
        }
      }
    }

    const groups: DataGridGroupColumn<ConversationListItem>[] = Array.from(groupedMap.entries()).map(
      ([code, { name, columns }]) => ({
        id: `attrGroup_${code}`,
        header: name,
        panelOnly: true,
        columns,
      }),
    );

    return [...ungrouped, ...groups];
  }, [allAttributes]);


  const contentSearchColumns = useMemo<DataGridLeafColumn<ConversationListItem>[]>(() => {
    const mode = contentSearch?.mode ?? null;
    if (mode == null) return [];
    const hasQuery = contentSearch?.hasQuery ?? false;
    // Колонки живут только пока активен режим поиска по содержимому, их видимость не сохраняется —
    // крестик в заголовке им не даём, убираются они выключением самого режима поиска.
    const relevance: DataGridLeafColumn<ConversationListItem> = {
      id: 'relevance',
      accessor: 'relevance',
      header: t('dialogs:phraseSearch.relevance'),
      size: 110,
      enableSorting: true,
      enableHiding: false,
      cell: ({ row }) => renderRelevanceCell(row.relevance),
    };
    const snippetId = getSnippetColumnId(mode);
    const snippet: DataGridLeafColumn<ConversationListItem> = {
      id: snippetId,
      accessor: snippetId,
      header:
        mode === 'phrase' ? t('dialogs:phraseSearch.snippet') : t('dialogs:commentSearch.snippet'),
      size: 240,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => renderSnippetCell(getSnippetValue(row, mode), hasQuery),
    };
    return pickContentSearchColumns({ relevance, snippet }, hasQuery);
  }, [contentSearch?.mode, contentSearch?.hasQuery, t]);

  return useMemo(
    () => [
      ...contentSearchColumns,
      ...mainColumns,
      ...reportColumns,
      ...attributeColumns,
    ],
    [contentSearchColumns, mainColumns, reportColumns, attributeColumns],
  );
};
